import React from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { brand, radius } from "../../theme/businessFlowsTheme";
import TypeIcon from "../app/typeIcons";
import { tint } from "../../utils/viewConfig";
import { eyebrowSx } from "./ContextBand";

// What is actually in the library that is open - the panel that replaced
// "500+ Reference processes / E2E Architecture maps / AI / SAP S/4HANA
// ready". Those four boxes were marketing copy printed over every customer's
// database, true of none of them in particular.
//
// Everything here comes from the configuration the app already loads
// (GET /processes/view-config): the object types this database shows, under
// the admin's own labels, in the admin's own colours, in the admin's order.
//
// Figures are shown only where the server sent them. `viewConfig.forClient`
// strips object counts for client users, so a customer sees the types and no
// numbers - which is the truth - and nothing is ever estimated to fill the
// space.

const HOW_MANY = 6;

const countable = (type) => typeof type.objects === "number" && Number.isFinite(type.objects);

// The types a reader can actually reach from here: what search or the
// explorer shows, never a diagram node (a diagram is how an object is drawn,
// not something to browse beside it).
export const libraryTypes = (config) =>
  (config?.types || []).filter(
    (type) => type && !type.isDiagram && type.objectType !== "Root" && (type.inSearch !== false || type.inExplorer !== false)
  );

// Sorted for a reader: biggest first where the server counted them, else in
// the order an admin put them in.
export const orderedTypes = (types) =>
  types.every(countable) ? [...types].sort((a, b) => b.objects - a.objects) : types;

// "12 object types · 1,842 objects" - the second half only where every type
// came with a count.
export const libraryFigures = (types) => {
  const typeCount = types.length;
  const counted = typeCount > 0 && types.every(countable);
  const objects = counted ? types.reduce((sum, type) => sum + type.objects, 0) : null;
  return { typeCount, objects };
};

const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? "" : "s"}`;

// "12 object types · 1,842 objects", or just the first half where the server
// sent no counts. One string, so it is one line to a reader and one node to
// a test.
export const figuresLine = ({ typeCount, objects }) =>
  typeCount > 0 ? `${plural(typeCount, "object type")}${objects === null ? "" : ` · ${plural(objects, "object")}`}` : "";

function TypeRow({ type, onOpen }) {
  const colour = type.colour || brand.ink;
  const body = (
    <>
      <Box aria-hidden="true" sx={{ width: 4, alignSelf: "stretch", borderRadius: 2, bgcolor: colour, flex: "0 0 auto" }} />
      <Box
        aria-hidden="true"
        sx={{ width: 30, height: 30, flex: "0 0 auto", borderRadius: radius.control, display: "grid", placeItems: "center", bgcolor: tint(colour, 0.12), color: colour }}
      >
        <TypeIcon name={type.icon} sx={{ fontSize: 18 }} />
      </Box>
      <Typography sx={{ minWidth: 0, flex: 1, fontWeight: 600, fontSize: "0.95rem", overflowWrap: "anywhere" }}>
        {type.label || type.objectType}
      </Typography>
      {countable(type) && (
        <Typography
          sx={{ flex: "0 0 auto", color: brand.muted, fontSize: "0.9rem", fontVariantNumeric: "tabular-nums" }}
        >
          {type.objects.toLocaleString()}
        </Typography>
      )}
    </>
  );
  const sx = {
    display: "flex",
    width: "100%",
    alignItems: "center",
    gap: 1.25,
    textAlign: "left",
    px: { xs: 1.75, md: 2 },
    py: 1.25,
    minHeight: 48,
    color: brand.ink,
  };
  if (!onOpen) return <Box sx={sx}>{body}</Box>;
  return (
    <ButtonBase
      onClick={onOpen}
      focusRipple
      aria-label={`Search ${type.label || type.objectType}`}
      sx={{
        ...sx,
        transition: "background-color .15s ease",
        "&:hover": { bgcolor: brand.shell },
        "&:focus-visible": { outline: `3px solid ${brand.signal}`, outlineOffset: -3 },
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      {body}
    </ButtonBase>
  );
}

export default function LibraryPanel({ config, unavailable, databaseName, onOpenType, onOpenAll, canImport }) {
  const all = orderedTypes(libraryTypes(config));
  const { typeCount, objects } = libraryFigures(all);
  const shown = all.slice(0, HOW_MANY);
  const rest = all.length - shown.length;

  return (
    <Box
      component="section"
      aria-labelledby="bf-library"
      sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mb: 1.25, flexWrap: "wrap" }}>
        <Typography component="h2" id="bf-library" sx={eyebrowSx}>
          In this library
        </Typography>
        {typeCount > 0 && (
          <Typography variant="body2" sx={{ color: brand.muted, fontVariantNumeric: "tabular-nums" }}>
            {figuresLine({ typeCount, objects })}
          </Typography>
        )}
      </Box>

      <Box sx={{ borderRadius: radius.surface, bgcolor: "#fff", border: `1px solid ${brand.line}`, overflow: "hidden", flex: "0 0 auto" }}>
        {/* The library's composition in one line, in the types' own colours.
            Drawn only where every type came with a count - never a picture of
            numbers we don't have. */}
        {objects > 0 && (
          <Box aria-hidden="true" sx={{ display: "flex", height: 6 }}>
            {all.map((type) => (
              <Box key={type.objectType} sx={{ flex: `${type.objects} 0 0`, minWidth: type.objects ? 2 : 0, bgcolor: type.colour || brand.ink }} />
            ))}
          </Box>
        )}

        {unavailable && (
          <Box sx={{ p: { xs: 2, md: 2.5 } }}>
            <Typography sx={{ fontWeight: 650 }}>This library couldn't be read</Typography>
            <Typography variant="body2" sx={{ color: brand.prose, mt: 0.5 }}>
              {unavailable.msg || "The database can't be reached right now. Try again shortly."}
            </Typography>
          </Box>
        )}

        {!unavailable && !config?.loaded && (
          <Box sx={{ p: { xs: 1.75, md: 2 } }} aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} height={28} sx={{ my: 0.75 }} />
            ))}
          </Box>
        )}

        {!unavailable && config?.loaded && all.length === 0 && (
          <Box sx={{ p: { xs: 2, md: 2.5 } }}>
            <Typography sx={{ fontWeight: 650 }}>Nothing has been imported yet</Typography>
            <Typography variant="body2" sx={{ color: brand.prose, mt: 0.5, maxWidth: "48ch" }}>
              {databaseName ? `${databaseName} has no object types to show.` : "This library has no object types to show."}{" "}
              {canImport
                ? "Import your processes under Console → Objects and diagrams, and they appear here."
                : "Ask bpExperts or your workspace admin to import your processes."}
            </Typography>
          </Box>
        )}

        {shown.map((type, i) => (
          <Box key={type.objectType} sx={{ borderTop: i === 0 && !(objects > 0) ? 0 : `1px solid ${brand.hairline}` }}>
            <TypeRow type={type} onOpen={onOpenType ? () => onOpenType(type) : null} />
          </Box>
        ))}

        {rest > 0 && (
          <Box sx={{ borderTop: `1px solid ${brand.hairline}`, px: { xs: 1.75, md: 2 }, py: 1.25 }}>
            {onOpenAll ? (
              <ButtonBase
                onClick={onOpenAll}
                focusRipple
                sx={{
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  color: brand.ink,
                  borderRadius: radius.control,
                  px: 0.5,
                  "&:focus-visible": { outline: `3px solid ${brand.signal}`, outlineOffset: 2 },
                }}
              >
                {`and ${rest} more — see them all in search`}
              </ButtonBase>
            ) : (
              <Typography variant="body2" sx={{ color: brand.muted }}>{`and ${rest} more`}</Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
